import { ref, toValue } from 'vue';
import {
  always, assoc, cond, has, identity, ifElse, isNil, pipe, prop, propEq, T, tap, when,
} from 'ramda';
import sessionAwareApiCall from '@/services/api/sessionAwareApiCall';
import createRequestWith from '@/services/api/createRequestWith';
import i18n from '@/i18n';
import { useGlobalState } from '@/store/user.store';
import logInDevMode from '@/services/backend/logInDevMode';
import { backendUrl } from '../../../config';

const useCallEndPointWith = (endpoint) => {
  // eslint-disable-next-line no-shadow
  const requestEndpointPath = (endpoint, { queryStringParameters }) => pipe(
    () => `${backendUrl}/${toValue(endpoint)}`,
    ifElse(
      always(queryStringParameters),
      (baseUrl) => `${baseUrl}?${new URLSearchParams(queryStringParameters).toString()}`,
      identity,
    ),
  )();

  const endpointData = ref('');
  const isLoading = ref(false);
  const userFriendlyError = ref('');

  const report500StatusCode = when(
    propEq(500, 'status'),
    tap(() => { userFriendlyError.value = i18n.global.t('global.endpointError'); }),
  );

  const process = cond([
    [propEq(true, 'error'), tap((v) => { userFriendlyError.value = prop('message', v); })],
    [
      has('error'),
      pipe(
        prop('error'),
        tap((v) => { userFriendlyError.value = v; }),
      ),
    ],
    [T, tap((v) => { endpointData.value = v; })],
  ]);

  const callEndpoint = async (options) => {
    const { sessionId } = useGlobalState();

    endpointData.value = '';
    userFriendlyError.value = '';
    isLoading.value = true;

    try {
      const url = requestEndpointPath(endpoint, options);
      logInDevMode(`fetching: ${url}`);
      logInDevMode('options: ', options);
      const response = await sessionAwareApiCall(
        fetch(
          url,
          createRequestWith(assoc('sessionId', toValue(sessionId), options)),
        ),
      );

      if (isNil(response)) {
        throw new Error('Response is null');
      }

      if (!response.ok) {
        const errorDetails = await response.json();
        throw new Error(
          `HTTP error! Status: ${response.status}, ${JSON.stringify(errorDetails)}, ${response}`,
        );
      }

      logInDevMode(response);
      report500StatusCode(response);
      const responseData = await ifElse(
        propEq(204, 'status'),
        always(''),
        async (r) => r.json(),
      )(response);

      process(responseData);
      isLoading.value = false;

      return responseData;
    } catch (e) {
      userFriendlyError.value = i18n.global.t('global.endpointError');
      isLoading.value = false;

      logInDevMode(e);
      console.error('Catched Error:', e);
      throw e;
    }
  };

  return {
    endpointData,
    userFriendlyError,
    callEndpoint,
    isLoading,
  };
};

export default useCallEndPointWith;
